import {
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  PlusOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { Table, Button, Space, Tag, Tooltip, Popconfirm, Typography, Input, Card, Grid } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState, useCallback } from "react";

import {
  PCD_FORM_MODES,
  buildPublicPcdUrl,
  SNIPPET_API_ENDPOINTS,
  NOTIFICATION_MESSAGES,
  PCD_SNIPPET_LABELS,
  PCD_SNIPPET_COLUMN_TITLES,
  PCD_PAGINATION_CONFIG,
  PCD_LANGUAGE_ENUM
} from "./helper";
import PcdSnippetFormModal from "./PcdSnippetFormModal";
import { copyTextToClipboard } from "../../../utils/clipboardHelper";
import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";

const { Text } = Typography;
const { Search } = Input;

const PCD_PAGINATION_INITIAL_STATE = Object.freeze({
  totalItems: 0,
  totalPages: 0,
  currentPage: PCD_PAGINATION_CONFIG.DEFAULT_PAGE,
  pageSize: PCD_PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
  hasNextPage: false,
  hasPrevPage: false
});

const PCDSnippetsManagementDashboard = ({ userConfig, ROOT_URL }) => {
  const screens = Grid.useBreakpoint();
  const isMobileView = screens.md === false;

  const [pcdSnippetsList, setPcdSnippetsList] = useState([]);
  const [isFetchingSnippets, setIsFetchingSnippets] = useState(false);
  const [isSavingSnippet, setIsSavingSnippet] = useState(false);
  const [deletingSnippetId, setDeletingSnippetId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(PCD_PAGINATION_CONFIG.DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(PCD_PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  const [paginationInfo, setPaginationInfo] = useState(PCD_PAGINATION_INITIAL_STATE);

  const [isSnippetModalOpen, setIsSnippetModalOpen] = useState(false);
  const [snippetModalMode, setSnippetModalMode] = useState(PCD_FORM_MODES.CREATE);
  const [activeSnippet, setActiveSnippet] = useState(null);

  /**
   * Fetches paginated PCD snippets from the admin API.
   */
  const handleFetchSnippets = useCallback(() => {
    setIsFetchingSnippets(true);

    axios
      .get(`${ROOT_URL}/${SNIPPET_API_ENDPOINTS.PCD_BASE}`, {
        params: {
          page: currentPage,
          limit: pageSize,
          search: searchQuery
        }
      })
      .then((response) => {
        const { data: snippetsList, pagination } = response.data;
        setPcdSnippetsList(
          snippetsList.map((snippet) => ({
            ...snippet,
            key: snippet.snippetId
          }))
        );
        setPaginationInfo(pagination);
      })
      .catch((errorResponse) => {
        showErrorNotification(errorResponse);
      })
      .finally(() => {
        setIsFetchingSnippets(false);
      });
  }, [ROOT_URL, currentPage, pageSize, searchQuery]);

  /**
   * Calls the API to create a new PCD snippet.
   */
  const submitCreateRequest = useCallback(
    (snippetPayload) => {
      setIsSavingSnippet(true);

      axios
        .post(`${ROOT_URL}/${SNIPPET_API_ENDPOINTS.PCD_BASE}/create`, snippetPayload)
        .then(() => {
          showSuccessNotification(NOTIFICATION_MESSAGES.PCD_CREATE_SUCCESS);
          handleCloseModal();
          handleFetchSnippets();
        })
        .catch((errorResponse) => {
          showErrorNotification(errorResponse);
        })
        .finally(() => {
          setIsSavingSnippet(false);
        });
    },
    [ROOT_URL, handleFetchSnippets]
  );

  /**
   * Calls the API to update an existing PCD snippet.
   */
  const submitUpdateRequest = useCallback(
    (snippetId, snippetPayload) => {
      setIsSavingSnippet(true);

      axios
        .put(`${ROOT_URL}/${SNIPPET_API_ENDPOINTS.PCD_BASE}/${snippetId}`, snippetPayload)
        .then(() => {
          showSuccessNotification(NOTIFICATION_MESSAGES.PCD_UPDATE_SUCCESS);
          handleCloseModal();
          handleFetchSnippets();
        })
        .catch((errorResponse) => {
          showErrorNotification(errorResponse);
        })
        .finally(() => {
          setIsSavingSnippet(false);
        });
    },
    [ROOT_URL, handleFetchSnippets]
  );

  /**
   * Calls the API to delete a PCD snippet.
   */
  const submitDeleteRequest = useCallback(
    (snippetId) => {
      setDeletingSnippetId(snippetId);

      axios
        .delete(`${ROOT_URL}/${SNIPPET_API_ENDPOINTS.PCD_BASE}/${snippetId}`)
        .then(() => {
          showSuccessNotification(NOTIFICATION_MESSAGES.PCD_DELETE_SUCCESS);
          handleFetchSnippets();
        })
        .catch((errorResponse) => {
          showErrorNotification(errorResponse);
        })
        .finally(() => {
          setDeletingSnippetId(null);
        });
    },
    [ROOT_URL, handleFetchSnippets]
  );

  /**
   * Orchestrates form submission based on current mode.
   */
  const handleSaveSnippet = (snippetPayload) => {
    if (snippetModalMode === PCD_FORM_MODES.EDIT && activeSnippet) {
      submitUpdateRequest(activeSnippet.snippetId, snippetPayload);
    } else {
      submitCreateRequest(snippetPayload);
    }
  };

  const handleOpenCreateModal = () => {
    setSnippetModalMode(PCD_FORM_MODES.CREATE);
    setActiveSnippet(null);
    setIsSnippetModalOpen(true);
  };

  const handleOpenEditModal = (snippet) => {
    setSnippetModalMode(PCD_FORM_MODES.EDIT);
    setActiveSnippet(snippet);
    setIsSnippetModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsSnippetModalOpen(false);
    setActiveSnippet(null);
  };

  /**
   * Copies the public PCD URL to clipboard.
   * @param {string} slug - PCD snippet slug
   */
  const copyPublicUrl = async (slug) => {
    const hasCopied = await copyTextToClipboard(buildPublicPcdUrl(slug));
    if (!hasCopied) {
      showErrorNotification({
        response: { data: { message: NOTIFICATION_MESSAGES.URL_COPY_FAILURE } }
      });
      return;
    }
    showSuccessNotification(NOTIFICATION_MESSAGES.URL_COPY_SUCCESS);
  };

  const handleSearchChange = (searchValue) => {
    setSearchQuery(searchValue);
    setCurrentPage(1);
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const formatDateIST = (dateString) => {
    return dayjs(dateString).format("DD MMM YYYY, HH:mm");
  };

  /**
   * Extracts language labels from a snippet for display.
   * @param {Object} snippet - Flattened PCD snippet from API
   * @returns {Array} Language names present in the snippet
   */
  const getSnippetLanguages = (snippet) => {
    if (!snippet.implementations) {
      return [];
    }
    return snippet.implementations.map((implementation) => implementation.language);
  };

  const snippetTableColumns = [
    {
      title: PCD_SNIPPET_COLUMN_TITLES.PROBLEM_NAME,
      dataIndex: "problemName",
      key: "problemName",
      width: "18%",
      render: (problemName) => (
        <Tooltip title={problemName}>
          <Text strong ellipsis style={{ maxWidth: 180 }}>
            {problemName}
          </Text>
        </Tooltip>
      )
    },
    {
      title: PCD_SNIPPET_COLUMN_TITLES.CONTEST_LINK,
      dataIndex: "contestLink",
      key: "contestLink",
      width: "20%",
      render: (contestLink) => (
        <Tooltip title={contestLink}>
          <a
            href={contestLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1890ff" }}
          >
            {contestLink.length > 40 ? `${contestLink.substring(0, 40)}...` : contestLink}
          </a>
        </Tooltip>
      )
    },
    {
      title: PCD_SNIPPET_COLUMN_TITLES.PLATFORM,
      dataIndex: "platformName",
      key: "platformName",
      width: "8%",
      align: "center",
      render: (platformName) => (
        <Tag color="blue">{platformName.charAt(0).toUpperCase() + platformName.slice(1)}</Tag>
      )
    },
    {
      title: PCD_SNIPPET_COLUMN_TITLES.LANGUAGES,
      key: "languages",
      width: "12%",
      align: "center",
      render: (_, snippet) => {
        const languages = getSnippetLanguages(snippet);
        return (
          <Space size={4} wrap>
            {languages.map((language) => (
              <Tag key={language} color={language === PCD_LANGUAGE_ENUM.CPP ? "green" : "default"}>
                {language}
              </Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: PCD_SNIPPET_COLUMN_TITLES.CREATED_BY,
      dataIndex: "createdBy",
      key: "createdBy",
      width: "12%",
      align: "center",
      render: (createdBy) => <Tag color="orange">{createdBy || "--"}</Tag>
    },
    {
      title: PCD_SNIPPET_COLUMN_TITLES.CREATED_AT,
      dataIndex: "createdAt",
      key: "createdAt",
      width: "10%",
      align: "center",
      render: (createdAt) => formatDateIST(createdAt)
    },
    {
      title: PCD_SNIPPET_COLUMN_TITLES.UPDATED_AT,
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: "10%",
      align: "center",
      render: (updatedAt) => formatDateIST(updatedAt)
    },
    {
      title: PCD_SNIPPET_COLUMN_TITLES.ACTIONS,
      key: "actions",
      width: "12%",
      align: "center",
      fixed: isMobileView ? undefined : "right",
      render: (_, snippet) => (
        <Space size="small">
          <Tooltip title="Copy Public URL">
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => copyPublicUrl(snippet.slug)}
            />
          </Tooltip>

          {userConfig?.globalContests?.hasPcdSnippetsModifyAccess && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenEditModal(snippet)}
                />
              </Tooltip>

              <Popconfirm
                title="Delete PCD Snippet"
                description="Are you sure you want to delete this snippet?"
                onConfirm={() => submitDeleteRequest(snippet.snippetId)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingSnippetId === snippet.snippetId}
                />
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ];

  useEffect(() => {
    handleFetchSnippets();
  }, [handleFetchSnippets]);

  const hasModifyAccess = userConfig?.globalContests?.hasPcdSnippetsModifyAccess;
  const addSnippetButton = (
    <Button
      type="primary"
      icon={<PlusOutlined />}
      onClick={handleOpenCreateModal}
      block={isMobileView}
    >
      {PCD_SNIPPET_LABELS.ADD_BUTTON}
    </Button>
  );

  return (
    <Card
      title={PCD_SNIPPET_LABELS.TABLE_TITLE}
      style={{ marginTop: 24 }}
      extra={!isMobileView && hasModifyAccess && addSnippetButton}
    >
      {isMobileView && hasModifyAccess && <div style={{ marginBottom: 16 }}>{addSnippetButton}</div>}

      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder={PCD_SNIPPET_LABELS.SEARCH_PLACEHOLDER}
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={handleSearchChange}
          style={{ width: "min(400px, 100%)" }}
        />
      </div>

      <Table
        columns={snippetTableColumns}
        dataSource={pcdSnippetsList}
        loading={isFetchingSnippets}
        bordered
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: paginationInfo.totalItems,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} snippets`,
          pageSizeOptions: PCD_PAGINATION_CONFIG.PAGE_SIZE_OPTIONS,
          onChange: handlePageChange,
          onShowSizeChange: handlePageChange
        }}
        scroll={{ x: 1100 }}
      />

      {isSnippetModalOpen && (
        <PcdSnippetFormModal
          mode={snippetModalMode}
          isModalVisible={isSnippetModalOpen}
          onCancel={handleCloseModal}
          onSave={handleSaveSnippet}
          snippetData={activeSnippet}
          isSubmitting={isSavingSnippet}
        />
      )}
    </Card>
  );
};

export default PCDSnippetsManagementDashboard;
